import * as React from "react";
import { Box, Typography, useTheme, MenuItem, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import ReceiptIcon       from "@mui/icons-material/Receipt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon     from "@mui/icons-material/Inventory";
import CategoryIcon      from "@mui/icons-material/Category";
import FilterListIcon    from "@mui/icons-material/FilterList";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import DonutLargeIcon    from "@mui/icons-material/DonutLarge";

import LineChart     from "components/Graphs/LineChart";
import DoughnutChart from "components/Graphs/DoughnutChart";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { usePurchaseDashboard } from "api/hooks/dashboards";
import { useYearsList }         from "api/hooks/inventory";
import { currencyFormatter }    from "utilities/currencyFormatter";

const PERIODS = ["DAY", "WEEK", "MONTH", "QUARTER", "YEAR"];

const inputSx = (cardBg, border) => ({
  "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
  "& .MuiInputLabel-root": { fontSize: "12px" },
});

const SectionHeading = ({ icon, title, subtitle, accent, textPri, textSec }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "1rem" }}>
    <Box sx={{
      width: "32px", height: "32px", borderRadius: "8px",
      backgroundColor: "rgba(29,158,117,0.12)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: accent, flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: textPri, lineHeight: 1.2 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

const StatCard = ({ label, value, accent, textPri, textSec, cardBg, border }) => (
  <Box sx={{
    flex: "1 1 160px",
    backgroundColor: cardBg,
    border: `1px solid ${border}`,
    borderRadius: "12px",
    p: "1rem 1.25rem",
    display: "flex", flexDirection: "column", gap: "6px",
  }}>
    <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "20px", fontWeight: 500, color: textPri, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Box sx={{ height: "2px", width: "24px", borderRadius: "2px", backgroundColor: accent, mt: "2px" }} />
  </Box>
);

export default function PurchaseDashboard() {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { purchaseDashboard, setPeriodType, setPeriod } = usePurchaseDashboard(business_branch_id);
  const { yearList } = useYearsList();

  const [periodSelect, setPeriodSelect] = useState("DAY");
  const [quarterYear, setQuarterYear]   = useState(moment().format("YYYY"));

  const handlePeriodTypeSelect = (val) => {
    setPeriodSelect(val);
    setPeriodType(val);
  };

  const purchaseAmount         = purchaseDashboard?.purchase_aggregation?.purchase_amount  ?? 0;
  const totalNumberOfPurchases = purchaseDashboard?.purchase_count                         ?? 0;
  const highestPurchase        = purchaseDashboard?.purchase_aggregation?.max_purchase     ?? 0;
  const averagePurchase        = purchaseDashboard?.purchase_aggregation?.average_purchase ?? 0;
  const purchasesInYear        = purchaseDashboard?.year_purchase_record ?? [];
  const supplierList           = purchaseDashboard?.supplier_metrics     ?? [];
  const productsList           = purchaseDashboard?.top_products         ?? [];
  const categoryList           = purchaseDashboard?.top_categories       ?? [];
  const purchaseList           = purchaseDashboard?.top_purchase         ?? [];

  const suppliers    = supplierList.map((s) => s.name);
  const supplierData = supplierList.map((s) => s.total_purchase_amount);

  const doughnutColors = [
    "#1D9E75","#0F6E56","#5DCAA5","#9FE1CB","#085041",
    "#04342C","#E1F5EE","#3AB9EA","#108ED4","#57903F",
  ];

  const periodLabel = periodSelect === "MONTH" ? "Monthly" : periodSelect === "WEEK" ? "Weekly" : "";

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <ReceiptIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Purchase Dashboard
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {moment().format("dddd, D MMMM YYYY")}
            </Typography>
          </Box>
        </Box>

        {/* Filter bar */}
        <Box sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
          p: "0.75rem 1rem",
          backgroundColor: cardBg,
          border: `1px solid ${border}`,
          borderRadius: "10px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
            <FilterListIcon sx={{ fontSize: "16px" }} />
            <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Period
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "5px" }}>
            {PERIODS.map((p) => (
              <Box key={p} onClick={() => handlePeriodTypeSelect(p)} sx={{
                px: "0.65rem", py: "0.25rem", borderRadius: "20px",
                fontSize: "12px", fontWeight: 500, cursor: "pointer",
                border: `1px solid ${periodSelect === p ? accent : border}`,
                color: periodSelect === p ? accent : textSec,
                backgroundColor: periodSelect === p ? "rgba(29,158,117,0.1)" : "transparent",
                transition: "all 0.15s",
                "&:hover": { borderColor: accent, color: accent },
              }}>
                {p.charAt(0) + p.slice(1).toLowerCase()}
              </Box>
            ))}
          </Box>
          {periodSelect === "DAY" && (
            <TextField label="Date" size="small" type="date"
              defaultValue={moment().format("YYYY-MM-DD")}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "WEEK" && (
            <TextField label="Week" size="small" type="week"
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "MONTH" && (
            <TextField label="Month" size="small" type="month"
              defaultValue={moment().format("YYYY-MM")}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "YEAR" && (
            <TextField label="Year" size="small" select
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "110px" }}>
              {yearList.map((y) => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
          )}
          {periodSelect === "QUARTER" && (
            <>
              <TextField label="Quarter" size="small" select
                onChange={(e) => setPeriod(`${quarterYear}-${e.target.value}`)}
                sx={{ ...inputSx(cardBg, border), width: "100px" }}>
                {["Q1","Q2","Q3","Q4"].map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
              </TextField>
              <TextField label="Year" size="small" select
                defaultValue={moment().format("YYYY")}
                onChange={(e) => setQuarterYear(e.target.value)}
                sx={{ ...inputSx(cardBg, border), width: "100px" }}>
                {yearList.map((y) => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
            </>
          )}
        </Box>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        {[
          { label: "Purchases Made",            value: totalNumberOfPurchases },
          { label: "Total Purchase Amount",     value: currencyFormatter(parseFloat(purchaseAmount)) },
          { label: "Highest Recorded Purchase", value: currencyFormatter(parseFloat(highestPurchase)) },
          { label: "Average Purchase",          value: currencyFormatter(parseFloat(averagePurchase)) },
        ].map(({ label, value }) => (
          <StatCard key={label} label={label} value={value}
            accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        ))}
      </Box>

      {/* ── Charts row ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{
          flex: "2 1 400px",
          backgroundColor: cardBg, border: `1px solid ${border}`,
          borderRadius: "12px", p: "1.25rem 1.5rem",
        }}>
          <SectionHeading
            icon={<TrendingUpIcon sx={{ fontSize: "18px" }} />}
            title="Monthly Purchase Amounts"
            subtitle="Year-to-date performance"
            accent={accent} textPri={textPri} textSec={textSec}
          />
          <LineChart
            label={["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]}
            data={[{
              label: "Purchases",
              data: purchasesInYear,
              fill: false,
              borderColor: accent,
              backgroundColor: "rgba(29,158,117,0.08)",
              tension: 0.3,
            }]}
          />
        </Box>

        <Box sx={{
          flex: "1 1 280px",
          backgroundColor: cardBg, border: `1px solid ${border}`,
          borderRadius: "12px", p: "1.25rem 1.5rem",
        }}>
          <SectionHeading
            icon={<DonutLargeIcon sx={{ fontSize: "18px" }} />}
            title={`${periodLabel} Purchases by Supplier`}
            subtitle={`${supplierList.length} suppliers`}
            accent={accent} textPri={textPri} textSec={textSec}
          />
          <DoughnutChart
            label={suppliers}
            data={[{
              label: "Supplier Payments",
              data: supplierData.length ? supplierData : [0],
              backgroundColor: doughnutColors.map((c) => `${c}33`),
              borderColor: doughnutColors,
              borderWidth: 1,
            }]}
          />
        </Box>
      </Box>

      {/* ── Tables row 1 ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", p: "1.25rem 1.25rem 0.75rem", overflow: "hidden" }}>
          <SectionHeading icon={<LocalShippingIcon sx={{ fontSize: "18px" }} />}
            title="Top Suppliers" subtitle={`${supplierList.length} suppliers`}
            accent={accent} textPri={textPri} textSec={textSec} />
          <ReportTable
            columns={[{ label: "Supplier" }, { label: "Purchases Made", align: "center" }, { label: "Amount", align: "right" }]}
            rows={supplierList}
            emptyMessage="No supplier data found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.name}</RCell>
                <RCell align="center">{row.purchases_made}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.total_purchase_amount))}</RCell>
              </RRow>
            )}
          />
        </Box>

        <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", p: "1.25rem 1.25rem 0.75rem", overflow: "hidden" }}>
          <SectionHeading icon={<InventoryIcon sx={{ fontSize: "18px" }} />}
            title="Top Products Purchased" subtitle={`${productsList.length} products`}
            accent={accent} textPri={textPri} textSec={textSec} />
          <ReportTable
            columns={[{ label: "Item" }, { label: "Qty Purchased", align: "center" }, { label: "Amount", align: "right" }]}
            rows={productsList}
            emptyMessage="No product data found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.product_name}</RCell>
                <RCell align="center">{row.quantity_purchased}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.purchase_amount))}</RCell>
              </RRow>
            )}
          />
        </Box>
      </Box>

      {/* ── Tables row 2 ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", p: "1.25rem 1.25rem 0.75rem", overflow: "hidden" }}>
          <SectionHeading icon={<ReceiptIcon sx={{ fontSize: "18px" }} />}
            title="Top Purchases" subtitle={`${purchaseList.length} records`}
            accent={accent} textPri={textPri} textSec={textSec} />
          <ReportTable
            columns={[
              { label: "Invoice #" }, { label: "Date" },
              { label: "Purchased By" }, { label: "Supplier" },
              { label: "Amount", align: "right" },
            ]}
            rows={purchaseList}
            emptyMessage="No purchase records found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.invoice_number}</RCell>
                <RCell>{row.purchase_date}</RCell>
                <RCell>{row.purchase_person}</RCell>
                <RCell>{row.supplied_by}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.grand_total))}</RCell>
              </RRow>
            )}
          />
        </Box>

        <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", p: "1.25rem 1.25rem 0.75rem", overflow: "hidden" }}>
          <SectionHeading icon={<CategoryIcon sx={{ fontSize: "18px" }} />}
            title="Top Categories Purchased" subtitle={`${categoryList.length} categories`}
            accent={accent} textPri={textPri} textSec={textSec} />
          <ReportTable
            columns={[{ label: "Category" }, { label: "Qty Purchased", align: "center" }, { label: "Amount", align: "right" }]}
            rows={categoryList}
            emptyMessage="No category data found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.category_name}</RCell>
                <RCell align="center">{row.quantity_purchased}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.purchase_amount))}</RCell>
              </RRow>
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}